import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { styled } from '@compiled/react';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import { LineChart, ScatterChart } from 'echarts/charts';
import {
	AxisPointerComponent,
	MarkAreaComponent,
	MarkLineComponent,
	TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useIsReducedMotion } from '@atlaskit/motion';
import { colors } from '@atlaskit/theme';
import { getTokenValue, token, useThemeObserver } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { FireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	ESTIMATION_ISSUE_COUNT,
	ESTIMATION_ORIGINAL_ESTIMATE,
} from '@atlassian/jira-software-report-charting-common/src/common/utils/format-estimate/index.tsx';
import ChartLegendItem from '@atlassian/jira-software-report-charting-common/src/ui/chart-legend/chart-legend-item/index.tsx';
import ChartLegend from '@atlassian/jira-software-report-charting-common/src/ui/chart-legend/index.tsx';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import type { BurnupChartData, SprintReportsEstimationStatistic } from '../../common/types';
import messages from './messages';
import { makeRenderTooltip } from './tooltip';
import { getOptions } from './utils';

echarts.use([
	LineChart,
	ScatterChart,
	AxisPointerComponent,
	MarkAreaComponent,
	MarkLineComponent,
	TooltipComponent,
	CanvasRenderer,
]);

type Props = {
	burnup: BurnupChartData;
	statisticField: null | SprintReportsEstimationStatistic;
	now: Date | null;
	userTimeZone: string;
	timeTrackingOptions: TimeTrackingOptions;
	onExperienceSuccess: () => void;
};

export const Chart = ({
	burnup,
	statisticField,
	now,
	userTimeZone,
	timeTrackingOptions,
	onExperienceSuccess,
}: Props) => {
	const intl = useIntl();
	useThemeObserver();

	const [isMouseOver, setIsMouseOver] = useState(false);
	const renderTooltip = useMemo(
		() => makeRenderTooltip({ intl, statisticField, userTimeZone, timeTrackingOptions }),
		[intl, statisticField, userTimeZone, timeTrackingOptions],
	);
	const prefersReducedMotion = useIsReducedMotion();

	const option = getOptions({
		renderTooltip,
		burnup,
		isMouseOver,
		statisticField,
		now: now || new Date(),
		intl,
		userTimeZone,
		prefersReducedMotion,
	});

	const setMouseTrue = useCallback(() => setIsMouseOver(true), []);
	const setMouseFalse = useCallback(() => setIsMouseOver(false), []);

	useEffect(() => {
		onExperienceSuccess();
	}, [onExperienceSuccess]);

	const getCompletedWorkMessage = (field: null | SprintReportsEstimationStatistic) => {
		if (field === ESTIMATION_ISSUE_COUNT) {
			return intl.formatMessage(messages.remainingIssuesDescription);
		}
		if (field === ESTIMATION_ORIGINAL_ESTIMATE) {
			return intl.formatMessage(messages.remainingTimeDescription);
		}
		return intl.formatMessage(messages.remainingStoriesDescription);
	};

	const getScopeMessage = (field: null | SprintReportsEstimationStatistic) => {
		if (field === ESTIMATION_ISSUE_COUNT) {
			return intl.formatMessage(messages.scopeIssues);
		}
		if (field === ESTIMATION_ORIGINAL_ESTIMATE) {
			return intl.formatMessage(messages.scopeTime);
		}
		return intl.formatMessage(messages.scopeStories);
	};

	return (
		<ChartContainer
			data-test-id="software-burnup-report.ui.chart.burnup-chart-container"
			onFocus={setMouseTrue}
			onBlur={setMouseFalse}
			onMouseOver={setMouseTrue}
			onMouseEnter={setMouseTrue}
			onMouseOut={setMouseFalse}
			onMouseLeave={setMouseFalse}
		>
			<ChartLegend>
				<ChartLegendItem
					name={intl.formatMessage(messages.completedName)}
					description={getCompletedWorkMessage(statisticField)}
					color={getTokenValue('color.chart.success', colors.G400)}
				/>
				<ChartLegendItem
					name={intl.formatMessage(messages.guidelineName)}
					description={intl.formatMessage(messages.guidelineDescription)}
					color={getTokenValue('color.chart.neutral', colors.N60)}
				/>
				<ChartLegendItem
					name={intl.formatMessage(messages.scope)}
					description={getScopeMessage(statisticField)}
					color={getTokenValue('color.chart.danger', colors.R400)}
				/>
			</ChartLegend>

			<ReactEchartsCore
				echarts={echarts}
				option={option}
				// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={{ width: '100%', height: 500 }}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="software-burnup-report-ui-chart-burnup-chart"
			/>
			<FireTrackAnalytics eventName="sprintBurnupChart loaded" />
		</ChartContainer>
	);
};

export default Chart;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChartContainer = styled.div({
	marginTop: token('space.300', '24px'),
	position: 'relative',
});
